import { Button, ButtonDropdown, DropdownItem, DropdownMenu, DropdownToggle, Modal, ModalBody, ModalFooter } from "reactstrap";
import React, { useCallback, useEffect, useReducer, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { makeRequest } from "../../../helpers/api";
import { Actor, Character, Characters, EditorState } from "../../../types";

import { adjustForAppearanceAnchorChange, changeCharacterAppearanceName, createCharacterAppearance, upsertCharacter } from "../../actions/characters-actions";
import { paintCharacterAppearance } from "../../actions/ui-actions";
import { isTextInput, makeId } from "../../utils/utils";

import AIModal, { AIMode } from "./ai-modal";
import NameModal from "./name-modal";
import { PaintModel, TOOLS_LIST } from "./paint-model";
import PixelCanvas from "./pixel-canvas";
import PixelColorPicker from "./pixel-color-picker";
import { PixelToolSize } from "./pixel-tool-size";
import PixelToolbar from "./pixel-toolbar";
import SpriteVariablesPanel from "./sprite-variables-panel";
import VariableOverlay from "./variable-overlay";

const SAVE_ERROR_MESSAGE =
  `Sorry, an error occurred and we're unable to save your image. Did you copy/paste,` +
  ` import from a file, or use the AI Generation feature? Please let me know at` +
  ` ben@foundry376.com and include what browser you're using!`;

const PaintContainer: React.FC = () => {
  const reduxDispatch = useDispatch();

  // Redux selectors
  const { characterId, appearanceId } = useSelector<
    EditorState,
    { characterId: string | null; appearanceId: string | null }
  >((s) => s.ui.paint);
  const characters = useSelector<EditorState, Characters>((s) => s.characters);
  const selectedActors = useSelector<EditorState, EditorState["ui"]["selectedActors"]>(
    (s) => s.ui.selectedActors,
  );
  const world = useSelector<EditorState, EditorState["world"]>((s) => s.world);

  // Get current character
  const character: Character | null = characterId ? characters[characterId] : null;

  // Get current actor from selection
  const currentActor: Actor | null = (() => {
    if (
      selectedActors?.worldId &&
      selectedActors?.stageId &&
      selectedActors.actorIds.length === 1
    ) {
      const stage = world.stages[selectedActors.stageId];
      if (stage?.actors[selectedActors.actorIds[0]]) {
        return stage.actors[selectedActors.actorIds[0]];
      }
    }
    return null;
  })();

  // Create model once and subscribe to changes
  const modelRef = useRef<PaintModel | null>(null);
  if (!modelRef.current) {
    modelRef.current = new PaintModel();
  }
  const model = modelRef.current;

  // Force re-render when model state changes
  const [, forceUpdate] = useReducer((x) => x + 1, 0);
  useEffect(() => model.subscribe(forceUpdate), [model]);

  // Load character data when it changes
  useEffect(() => {
    if (character && appearanceId) {
      model.loadFromCharacter(character, appearanceId);
    } else {
      model.reset();
    }
  }, [character, appearanceId, model]);

  // Clipboard event handlers - only active while the paint modal is open
  useEffect(() => {
    if (!characterId) return;

    const handleCopy = (e: Event) => {
      if (isTextInput(e.target)) return;
      e.preventDefault();
      model.copy();
    };
    const handleCut = (e: Event) => {
      if (isTextInput(e.target)) return;
      e.preventDefault();
      model.cut();
    };
    const handlePaste = (e: Event) => {
      if (isTextInput(e.target)) return;
      e.preventDefault();
      model.paste();
    };

    document.body.addEventListener("copy", handleCopy);
    document.body.addEventListener("cut", handleCut);
    document.body.addEventListener("paste", handlePaste);

    return () => {
      document.body.removeEventListener("copy", handleCopy);
      document.body.removeEventListener("cut", handleCut);
      document.body.removeEventListener("paste", handlePaste);
    };
  }, [model, characterId]);

  // File input ref
  const focusRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Event handlers - simple delegation to model
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      // Don't intercept keyboard events if user is typing in an input field
      const target = event.target as HTMLElement;
      if (target.tagName === "INPUT" || target.tagName === "TEXTAREA") {
        return;
      }
      if (model.handleKeyDown(event.nativeEvent)) {
        event.preventDefault();
        event.stopPropagation();
      }
    },
    [model],
  );

  const handleDuplicateAppearance = useCallback(() => {
    if (!characterId || !character || !appearanceId) return;
    const { spritesheet } = character;
    const existingData = spritesheet.appearances[appearanceId];
    const newAppearanceId = makeId("appearance");
    reduxDispatch(createCharacterAppearance(characterId, newAppearanceId, existingData ? existingData[0] : null));
    reduxDispatch(paintCharacterAppearance(characterId, newAppearanceId));
  }, [characterId, character, appearanceId, reduxDispatch]);

  const handleClose = useCallback(() => {
    reduxDispatch(paintCharacterAppearance(null));
  }, [reduxDispatch]);

  const [nameModalOpen, setNameModalOpen] = useState(false);
  const [proposedName, setProposedName] = useState("");
  const [isGeneratingName, setIsGeneratingName] = useState(false);

  const saveAndClose = useCallback((generatedSpriteName?: string) => {
    if (!characterId || !appearanceId || !character) return;

    const saveData = model.getSaveData(character, appearanceId);
    if (!saveData) {
      alert(SAVE_ERROR_MESSAGE);
      return;
    }

    // Close modal first
    setNameModalOpen(false);
    reduxDispatch(paintCharacterAppearance(null));

    // If the anchor square moved, shift every rule and placed actor that uses
    // this appearance so the artwork stays in exactly the same squares.
    const prevAnchor = character.spritesheet.appearanceInfo?.[appearanceId]?.anchor || {
      x: 0,
      y: 0,
    };

    // Update character data after small delay
    setTimeout(() => {
      reduxDispatch(
        adjustForAppearanceAnchorChange(
          characterId,
          appearanceId,
          prevAnchor,
          saveData.anchorSquare,
        ),
      );
      reduxDispatch(
        upsertCharacter(characterId, {
          name: generatedSpriteName || character.name,
          spritesheet: {
            appearances: {
              [appearanceId]: [saveData.imageDataURL],
            },
            appearanceNames: {
              [appearanceId]:
                generatedSpriteName || character.spritesheet.appearanceNames?.[appearanceId],
            },
            appearanceInfo: {
              [appearanceId]: {
                anchor: saveData.anchorSquare,
                filled: saveData.filled,
                width: saveData.width,
                height: saveData.height,
                variableOverlay: saveData.variableOverlay,
              },
            },
          },
        }),
      );
    }, 10);
  }, [characterId, appearanceId, character, reduxDispatch, model]);

  // Unnamed sprites are named by the user before saving, with an AI proposal
  // prefilled from the artwork and (if it was drawn with AI) the user's prompt.
  const handleCloseAndSave = useCallback(async () => {
    if (!characterId || !appearanceId || !character) return;

    if (character.name.toLowerCase() !== "untitled") {
      saveAndClose();
      return;
    }

    const saveData = model.getSaveData(character, appearanceId);
    if (!saveData) {
      alert(SAVE_ERROR_MESSAGE);
      return;
    }

    const { spriteName, aiPrompt } = model.getState();
    setProposedName("");
    setIsGeneratingName(true);
    setNameModalOpen(true);

    try {
      const resp = await makeRequest<{ name?: string }>("/generate-sprite-name", {
        method: "POST",
        json: aiPrompt
          ? { prompt: aiPrompt }
          : { imageData: saveData.imageDataURL },
      });
      setProposedName((resp?.name || spriteName).toLowerCase());
    } catch (err) {
      console.error("Failed to auto-generate sprite name:", err);
      setProposedName(spriteName.toLowerCase());
    } finally {
      setIsGeneratingName(false);
    }
  }, [characterId, appearanceId, character, model, saveAndClose]);

  const handleChooseFile = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        model.handleFileSelect(file);
      }
    },
    [model],
  );

  const handleDownloadImage = useCallback(() => {
    if (character && appearanceId) {
      model.downloadImage(character.name, appearanceId);
    }
  }, [model, character, appearanceId]);

  const [aiMode, setAiMode] = useState<AIMode | null>(null);

  // Get current state from model
  const state = model.getState();
  const { imageData, tool, toolSize, color, undoStack, redoStack } = state;

  const handleOpened = useCallback(() => {
    focusRef.current?.focus();
  }, []);

  return (
    <Modal
      isOpen={imageData !== null}
      backdrop="static"
      toggle={() => {}}
      className="paint"
      onOpened={handleOpened}
    >
      <div tabIndex={0} onKeyDown={handleKeyDown} ref={focusRef}>
        <input
          id="hiddenFileInput"
          ref={fileInputRef}
          accept="image/*"
          type="file"
          style={{ position: "fixed", top: -1000 }}
          onChange={handleChooseFile}
          onFocus={(event) => (event.target.parentNode as HTMLElement)?.focus()}
        />
        <div className="modal-header" style={{ display: "flex", alignItems: "center" }}>
          <input
            style={{ flex: 1, fontSize: "1.25rem", fontWeight: 500, border: "none", background: "transparent", outline: "none", borderBottom: "1px solid transparent", width: "100%" }}
            value={character?.spritesheet.appearanceNames?.[appearanceId ?? ""] ?? ""}
            placeholder="Appearance Name"
            onFocus={(e) => (e.target.style.borderBottomColor = "#aaa")}
            onBlur={(e) => {
              e.target.style.borderBottomColor = "transparent";
              if (characterId && appearanceId) {
                reduxDispatch(changeCharacterAppearanceName(characterId, appearanceId, e.target.value));
              }
            }}
            onChange={(e) => {
              if (characterId && appearanceId) {
                reduxDispatch(changeCharacterAppearanceName(characterId, appearanceId, e.target.value));
              }
            }}
          />
          <Button
            title="Undo"
            className="icon"
            onClick={() => model.undo()}
            disabled={undoStack.length === 0}
          >
            <img src={new URL("../../img/icon_undo.png", import.meta.url).href} />
          </Button>
          <Button
            title="Redo"
            className="icon"
            onClick={() => model.redo()}
            disabled={redoStack.length === 0}
          >
            <img src={new URL("../../img/icon_redo.png", import.meta.url).href} />
          </Button>
          <ButtonDropdown
            isOpen={state.dropdownOpen}
            toggle={() => model.setDropdownOpen(!state.dropdownOpen)}
          >
            <DropdownMenu right>
              <DropdownItem onClick={() => model.selectAll()}>Select All</DropdownItem>
              {state.selectionImageData ? (
                <DropdownItem onClick={() => model.clearSelection()}>Clear Selection</DropdownItem>
              ) : (
                <DropdownItem onClick={() => model.clearAll()}>Clear All</DropdownItem>
              )}
              <DropdownItem disabled={!state.selectionImageData} onClick={() => model.cut()}>
                Cut Selection
              </DropdownItem>
              <DropdownItem onClick={() => model.copy()}>
                {state.selectionImageData ? "Copy Selection" : "Copy Image"}
              </DropdownItem>
              <DropdownItem onClick={() => model.paste()}>Paste</DropdownItem>
              <DropdownItem divider />
              <DropdownItem onClick={() => model.flipHorizontally()}>
                Flip Horizontally
              </DropdownItem>
              <DropdownItem onClick={() => model.flipVertically()}>Flip Vertically</DropdownItem>
              <DropdownItem onClick={() => model.rotate90()}>Rotate 90º</DropdownItem>
              <DropdownItem onClick={() => model.rotateNeg90()}>Rotate -90º</DropdownItem>
              <DropdownItem divider />
              <DropdownItem
                onClick={() => model.chooseAnchorSquare()}
                disabled={imageData !== null && imageData.width === 40 && imageData.height === 40}
              >
                Choose Anchor Square
              </DropdownItem>
              <DropdownItem divider />
              <label htmlFor="hiddenFileInput" className="dropdown-item" style={{ margin: 0 }}>
                Import Image from File...
              </label>
              <DropdownItem onClick={handleDownloadImage}>Download Sprite as PNG</DropdownItem>
              <DropdownItem divider />
              <DropdownItem onClick={handleDuplicateAppearance}>
                Duplicate as New Appearance...
              </DropdownItem>
            </DropdownMenu>
            <DropdownToggle className="icon">
              <i className="fa fa-ellipsis-v" />
            </DropdownToggle>
          </ButtonDropdown>
        </div>
        <ModalBody>
          <div className="flex-horizontal" style={{ gap: 8 }}>
            <div className="paint-sidebar">
              <PixelColorPicker
                color={color}
                onColorChange={(c) => model.setColor(c)}
                supported={tool.supportsColor()}
              />
              <PixelToolbar
                tools={TOOLS_LIST}
                tool={tool}
                onToolChange={(t) => model.chooseTool(t)}
              />
              <PixelToolSize
                tool={tool}
                size={toolSize}
                onSizeChange={(size) => model.setToolSize(size)}
              />

              <Button size="sm" style={{ width: 114 }} onClick={() => model.clearAll()}>
                Clear Canvas
              </Button>

              <SpriteVariablesPanel
                character={character}
                actor={currentActor}
                showVariables={state.showVariables}
                visibleVariables={state.visibleVariables}
                onToggleVariableVisibility={(id) => model.toggleVariableVisibility(id)}
              />
            </div>
            <div className="canvas-arrows-flex">
              <Button
                className="canvas-arrow"
                size="sm"
                onClick={() => model.updateCanvasSize(0, 1, 0, 1)}
              >
                +
              </Button>
              <Button
                className="canvas-arrow"
                size="sm"
                disabled={imageData !== null && imageData.height <= 40}
                onClick={() => model.shrinkCanvas(0, -1, 0, -1)}
              >
                −
              </Button>
              <div className="canvas-arrows-flex" style={{ flexDirection: "row" }}>
                <Button
                  className="canvas-arrow"
                  size="sm"
                  onClick={() => model.updateCanvasSize(1, 0, 1, 0)}
                >
                  +
                </Button>
                <Button
                  className="canvas-arrow"
                  size="sm"
                  disabled={imageData !== null && imageData.width <= 40}
                  onClick={() => model.shrinkCanvas(-1, 0, -1, 0)}
                >
                  −
                </Button>
                <div style={{ position: "relative" }}>
                  <PixelCanvas
                    model={model}
                    tool={tool}
                    color={color}
                    toolSize={toolSize}
                    pixelSize={state.pixelSize}
                    anchorSquare={state.anchorSquare}
                    imageData={state.imageData}
                    selectionImageData={state.selectionImageData}
                    selectionOffset={state.selectionOffset}
                    interaction={state.interaction}
                    interactionPixels={state.interactionPixels}
                  />
                  <VariableOverlay
                    character={character}
                    actor={currentActor}
                    showVariables={state.showVariables}
                    visibleVariables={state.visibleVariables}
                    pixelSize={state.pixelSize}
                    imageData={state.imageData}
                  />
                </div>
                <div
                  style={{
                    height: "100%",
                    display: "grid",
                    gap: 20,
                    gridTemplateRows: "1fr auto 1fr",
                  }}
                >
                  <span />
                  <div className="canvas-arrows-flex" style={{ flexDirection: "row" }}>
                    <Button
                      size="sm"
                      className="canvas-arrow"
                      disabled={imageData !== null && imageData.width <= 40}
                      onClick={() => model.shrinkCanvas(-1, 0, 0, 0)}
                    >
                      −
                    </Button>
                    <Button
                      size="sm"
                      className="canvas-arrow"
                      onClick={() => model.updateCanvasSize(1, 0, 0, 0)}
                    >
                      +
                    </Button>
                  </div>
                  <div>
                    <input
                      type="range"
                      min={1}
                      max={11}
                      style={{ writingMode: "vertical-rl", marginLeft: 14 }}
                      value={state.pixelSize}
                      onChange={(e) => model.setPixelSize(Number(e.currentTarget.value))}
                    />
                  </div>
                </div>
              </div>
              <Button
                className="canvas-arrow"
                size="sm"
                disabled={imageData !== null && imageData.height <= 40}
                onClick={() => model.shrinkCanvas(0, -1, 0, 0)}
              >
                −
              </Button>
              <Button
                className="canvas-arrow"
                size="sm"
                onClick={() => model.updateCanvasSize(0, 1, 0, 0)}
              >
                +
              </Button>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button onClick={() => setAiMode("draw")}>
            <i className="fa fa-paint-brush" style={{ color: "#7b5ea7" }} /> Draw with AI
          </Button>{" "}
          <Button
            onClick={() => setAiMode("edit")}
            disabled={!imageData}
            title={imageData ? undefined : "Draw or import something first to edit it with AI"}
          >
            <i className="fa fa-pencil" style={{ color: "#7b5ea7" }} /> Edit with AI
          </Button>
          <div style={{ flex: 1 }} />
          <Button key="cancel" onClick={handleClose}>
            Cancel
          </Button>{" "}
          <Button
            color="primary"
            key="save"
            data-tutorial-id="paint-save-and-close"
            onClick={handleCloseAndSave}
          >
            Done
          </Button>
        </ModalFooter>
      </div>
      <AIModal
        model={model}
        isOpen={aiMode !== null}
        mode={aiMode ?? "draw"}
        onClose={() => setAiMode(null)}
      />
      <NameModal
        isOpen={nameModalOpen}
        proposedName={proposedName}
        isGenerating={isGeneratingName}
        onSave={(name) => saveAndClose(name)}
        onCancel={() => setNameModalOpen(false)}
      />
    </Modal>
  );
};

export default PaintContainer;
